import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  Loader,
  Alert,
  Paper,
  Tabs,
  Card,
  Group,
  Stack,
  Anchor,
  Table,
  ScrollArea,
  ActionIcon,
  TextInput,
} from '@mantine/core';
import { IconSearch, IconDownload, IconPrinter } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../../supply-chain/purchasing/SQLEditorPopup';

const ColorLegend = () => (
  <Card withBorder radius="md" style={{ width: '350px' }}>
    <Text size="sm" fw={500} mb="xs">
      Legend
    </Text>
    <Stack gap="xs">
      {[
        { color: '#73A57D', label: 'Boiling - Sales each week for the past 90 days (13 weeks)' },
        { color: '#73A57D', label: 'Hot - Sales for each of the past 3 months' },
        { color: '#D1A752', label: 'Warm - Sales in the last month or in both the two previous months' },
        { color: '#D6937E', label: 'Cold - Sales in the past year' },
        { color: '#5d90dc', label: 'Ice - No sales in a year' },
      ].map(({ color, label }) => (
        <Group key={label} gap="xs" align="flex-start">
          <Box
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: color,
              borderRadius: '2px',
              flexShrink: 0,
              marginTop: '2px',
            }}
          />
          <Text size="xs" c="dimmed" style={{ flex: 1, lineHeight: 1.3 }}>
            {label}
          </Text>
        </Group>
      ))}
      <Text size="xs" c="dimmed" mt="xs">
        ✔️ indicates the account is owned by the salesperson
      </Text>
    </Stack>
  </Card>
);

const SalesPerformanceScorecardRevenueDetail = () => {
  const { ccode, mode } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useAuthenticatedData(`${config.apiBaseUrl}/sales/scorecard/${ccode}/revenue${mode.toUpperCase()}`);
  const { data: salesPersonName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

  // State for pop-ups and search
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Clear handlers for proper dialog closing
  const handleCloseMembership = () => setOpenMembership(false);
  const handleCloseSummaryQuery = () => setOpenSummaryQuery(false);
  const handleCloseDetailsQuery = () => setOpenDetailsQuery(false);

  const { columns, rows, filteredRows } = useMemo(() => {
    if (!data) return { columns: [], rows: [], filteredRows: [] };

    const timePeriod = mode === 'qtd' ? 'week' : mode === 'ytd' ? 'month' : 'day';

    // Create a map to store unique periods with their timestamps
    const periodsMap = new Map();

    data.forEach(account => {
      account.activities.forEach(activity => {
        const date = new Date(activity.activityDate);
        let display, timestamp;

        if (timePeriod === 'week') {
          const startOfWeek = new Date(date);
          startOfWeek.setDate(date.getDate() - date.getDay());
          display = startOfWeek.toLocaleDateString();
          timestamp = startOfWeek.getTime();
        } else if (timePeriod === 'month') {
          display = `${date.getFullYear()}-${date.getMonth() + 1}`;
          timestamp = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
        } else {
          display = date.toLocaleDateString();
          timestamp = date.getTime();
        }

        if (!periodsMap.has(display)) {
          periodsMap.set(display, timestamp);
        }
      });
    });

    // Convert map to array and sort by timestamp (oldest first)
    const allPeriods = Array.from(periodsMap, ([display, timestamp]) => ({
      display,
      timestamp
    })).sort((a, b) => a.timestamp - b.timestamp);

    // Create rows and calculate totals
    const rows = data.map(account => {
      const rowData = {
        id: account.accountNumber,
        account: `${account.isOwned ? '✔️ ' : ''}${account.accountName} (${account.accountNumber})`,
        colorCategory: account.colorCategory,
        marketSegment: account.marketSegment,
        keyCustomer: `${account.isKeyCustomer === 'Y' ? '✔️ ' : ''}`,
        total: 0,
        periods: {}
      };

      // Initialize period totals
      allPeriods.forEach(period => {
        const periodAmount = account.activities
          .filter(activity => {
            const date = new Date(activity.activityDate);
            if (timePeriod === 'week') {
              const startOfWeek = new Date(date);
              startOfWeek.setDate(date.getDate() - date.getDay());
              return startOfWeek.toLocaleDateString() === period.display;
            } else if (timePeriod === 'month') {
              return `${date.getFullYear()}-${date.getMonth() + 1}` === period.display;
            }
            return date.toLocaleDateString() === period.display;
          })
          .reduce((sum, activity) => sum + activity.activityAmount, 0);

        rowData.periods[period.display] = periodAmount;
        rowData.total += periodAmount;
      });

      return rowData;
    });

    rows.sort((a, b) => a.account.localeCompare(b.account));

    // Filter rows based on search term
    const filteredRows = rows.filter(row =>
      !searchTerm ||
      row.account.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.marketSegment.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return { columns: allPeriods, rows, filteredRows };
  }, [data, mode, searchTerm]);

  const handleTabChange = (value) => {
    navigate(`/sales/salesperson/${ccode}/scorecard/revenue/${value}`);
  };

  const getRowColor = (colorCategory) => {
    switch (colorCategory?.toLowerCase()) {
      case 'ice': return '#5d90dc';
      case 'cold': return '#D6937E';
      case 'warm': return '#D1A752';
      case 'hot': return '#73A57D';
      case 'boiling': return '#73A57D';
      case 'total': return '#e0e0e0';
      default: return undefined;
    }
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return '';
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  };

  const exportToCSV = () => {
    if (!filteredRows.length || !columns.length) return;

    const headers = ['Account', 'Market Segment', 'Key Customer', ...columns.map(c => c.display), 'Total'];
    const csvContent = [
      headers.join(','),
      ...filteredRows.map(row => [
        `"${row.account}"`,
        `"${row.marketSegment}"`,
        `"${row.keyCustomer}"`,
        ...columns.map(c => row.periods[c.display] || 0),
        row.total
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'revenue-details.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading || isLoadingName) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  return (
    <Paper shadow="md" radius="md"  >
      <Stack gap="xs">
        {/* Header Section with Title and Helper Links */}
        <Box>
          <Text
            component="h1"
            style={{
              fontFamily: 'Orbitron, sans-serif',
              color: '#FDB813',
              fontStyle: 'italic',
              fontSize: '1.5rem',
              marginBottom: 0,
              textAlign: 'left'
            }}
          >
            Revenue Detail - {salesPersonName}
          </Text>
          {/* Technical options: small links */}
          <Group gap="xs" mb="sm">

            <Anchor
              size="xs"
              c="dimmed"
              onClick={() =>
                window.open(
                  '/query-commander/editor?queryKey=sales-individual-revenue-mtd',
                  '_blank'
                )
              }
              style={{ cursor: 'pointer' }}
            >
              Summary Query
            </Anchor>
            <Anchor
              size="xs"
              c="dimmed"
              onClick={() =>
                window.open(
                  '/query-commander/editor?queryKey=sales-individual-revenue-details-mtd',
                  '_blank'
                )
              }
              style={{ cursor: 'pointer' }}
            >
              Details Query
            </Anchor>
          </Group>

        </Box>

        {/* Info Cards - tighter spacing */}
        <Group gap="xs" align="stretch" style={{ marginBottom: '0.5rem' }}>
          <Box style={{ flex: 1, minWidth: '300px' }}>
            <Card withBorder radius="md" style={{ height: '100%' }}>
              <Text size="sm" fw={500} mb="xs">
                About the Data
              </Text>
              <Text size="xs" c="dimmed">
                These are all sales orders for {salesPersonName}, {mode.toUpperCase()}, regardless of whether the customers have been assigned to them.
              </Text>
            </Card>
          </Box>
          <ColorLegend />
        </Group>

        {/* Tabs - compact */}
        <Tabs
          value={mode}
          onChange={handleTabChange}
          styles={(theme) => ({
            tab: {
              color: theme.colorScheme === 'dark' ? '#b3b3b3' : '#666666',
              '&[data-active]': {
                color: theme.colorScheme === 'dark' ? '#ffffff' : '#000000',
                fontWeight: 'bold',
              },
              padding: '6px 12px',
            },
          })}
        >
          <Tabs.List>
            <Tabs.Tab value="mtd">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="qtd">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="ytd">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* Toolbar - reduced spacing */}
        <Group justify="space-between" mb="xs">
          <TextInput
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftSection={<IconSearch size={16} />}
            style={{ width: '300px' }}
            size="sm"
          />
          <Group gap="xs">
            <ActionIcon
              variant="default"
              onClick={exportToCSV}
              title="Export to CSV"
              size="sm"
            >
              <IconDownload size={16} />
            </ActionIcon>
            <ActionIcon
              variant="default"
              onClick={() => window.print()}
              title="Print"
              size="sm"
            >
              <IconPrinter size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Data Table - compact height */}
        <Paper withBorder style={{ height: '500px' }}>
          <ScrollArea style={{ height: '100%' }}>
            <Table striped highlightOnHover size="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '250px' }}>Account</Table.Th>
                  <Table.Th style={{ width: '150px' }}>Market Segment</Table.Th>
                  <Table.Th style={{ width: '100px' }}>Key Customer</Table.Th>
                  {columns.map(period => (
                    <Table.Th key={period.display} style={{ width: '130px' }}>
                      {period.display}
                    </Table.Th>
                  ))}
                  <Table.Th style={{ width: '130px' }}>Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredRows.map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={{
                      backgroundColor: getRowColor(row.colorCategory),
                    }}
                  >
                    <Table.Td>
                      <Text size="xs" fw={500} c="dark">
                        {row.account}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dark">
                        {row.marketSegment}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dark">
                        {row.keyCustomer}
                      </Text>
                    </Table.Td>
                    {columns.map(period => (
                      <Table.Td key={period.display}>
                        <Text size="xs" fw={600} c="dark">
                          {formatCurrency(row.periods[period.display])}
                        </Text>
                      </Table.Td>
                    ))}
                    <Table.Td>
                      <Text size="xs" fw={600} c="dark">
                        {formatCurrency(row.total)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>

        {/* Results count - minimal spacing */}
        <Text size="xs" c="dimmed">
          Showing {filteredRows.length} result{filteredRows.length !== 1 ? 's' : ''}
        </Text>
      </Stack>


    </Paper>
  );
};

export default SalesPerformanceScorecardRevenueDetail;